import React from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {SearchFormDesktop} from 'components/SearchBox/Home/SearchFormDesktop'

import {Box} from '@vio/atlas/helpers/Box'
import {PageWidthContainer} from '@vio/atlas/helpers/PageWidthContainer'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

const Wrapper = styled.div<{
  backgroundImage: string
  heroVerticalPadding?: string
}>`
  --hero-vertical-padding: ${({heroVerticalPadding}) =>
    heroVerticalPadding ?? cssTheme.layout.spacing.s950};
  background: ${cssTheme.colors.homepage.hero.background}
    ${({backgroundImage}) => `url(${backgroundImage}) center no-repeat`};
  background-size: cover;
  &.--offset-hero-image {
    background: ${({backgroundImage}) =>
        `url(${backgroundImage}) calc(50% + 144px) top no-repeat`},
      ${cssTheme.colors.homepage.hero.background};
  }
`

const Container = styled(PageWidthContainer)`
  padding-top: var(--hero-vertical-padding) !important;
  padding-bottom: var(--hero-vertical-padding) !important;
`

const Title = styled(Text)`
  margin-bottom: ${cssTheme.layout.spacing.s100};
  background-color: ${cssTheme.colors.homepage.hero.background};
  width: fit-content;

  &.--emphasized-title {
    ${cssTheme.typography.title2XL}
    text-transform: uppercase;
    font-weight: ${cssTheme.fonts.weight.lg};
    letter-spacing: 11px;
  }
`

const Subtitle = styled(Text)`
  background-color: ${cssTheme.colors.homepage.hero.background};
  width: fit-content;
`

const SearchFormWrapper = styled.div`
  background-color: ${cssTheme.colors.homepage.form.background};
  padding: ${cssTheme.layout.spacing.s600};
  border-radius: ${cssTheme.layout.radius.lg};
  &.--has-shadow {
    box-shadow: ${cssTheme.shadows.raised};
  }
`

export const AlternativeDesktopSearchForm: React.FC = () => {
  const {brandHomepageHeadingMessage, brandHomepageSubheadingMessage} =
    useBrand()
  const {shell} = useBrandCapabilities()
  const alternativeSearchFormSettings = shell.hasAlternativeSearchForm.enabled
    ? shell.hasAlternativeSearchForm.config
    : undefined

  const {
    hasTitle,
    hasSubtitle,
    hasShadow,
    heroImage,
    hasOffsetHeroImage,
    hasEmphasizedTitle,
    heroVerticalPadding
  } = alternativeSearchFormSettings || {}

  const assetsEndpoint = process.env.NEXT_PUBLIC_ASSETS_ENDPOINT
  const heroImgName = heroImage
    ? `${assetsEndpoint}static/${heroImage}`
    : 'none'

  return (
    <Wrapper
      backgroundImage={heroImgName}
      heroVerticalPadding={heroVerticalPadding}
      className={cx(hasOffsetHeroImage && '--offset-hero-image')}
    >
      <Container paddingDesktopXsSize="s500" paddingMobileSmSize="s400">
        {(hasTitle || hasSubtitle) && (
          <Box marginBottom="s800">
            {brandHomepageHeadingMessage && (
              <Title
                variant="title5XL"
                dataId="HomeHeading"
                colorPath="homepage.hero.content"
                className={cx(hasEmphasizedTitle && '--emphasized-title')}
              >
                <FormattedMessageWrapper {...brandHomepageHeadingMessage} />
              </Title>
            )}

            {brandHomepageSubheadingMessage && (
              <Subtitle variant="labelM" colorPath="homepage.hero.content">
                <FormattedMessageWrapper {...brandHomepageSubheadingMessage} />
              </Subtitle>
            )}
          </Box>
        )}
        <SearchFormWrapper className={cx(hasShadow && '--has-shadow')}>
          <SearchFormDesktop />
        </SearchFormWrapper>
      </Container>
    </Wrapper>
  )
}
